import CodeMirror, { type BasicSetupOptions } from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags } from "@lezer/highlight";
import React from "react";
import { eagerParsing, measureOnScroll } from "../../shared/utils/codemirror";

// These editors only ever render inside debugger tables, so they get a dedicated in-table look
// (transparent chrome, muted syntax colors) instead of the standard theme used elsewhere...
const TRANSPARENT_TABLE_THEME = EditorView.theme({
    "&": { backgroundColor: "transparent !important" },
    ".cm-gutters": { backgroundColor: "transparent !important", color: "var(--colors-grey-5) !important", border: "none !important" },
    ".cm-activeLineGutter": { backgroundColor: "transparent !important", color: "var(--colors-grey-5)" },
    ".cm-content": { padding: 0 },
    ".cm-line.cm-activeLine": { backgroundColor: "transparent" },
}, {
    dark: document.body.classList.contains("theme-dark")
});

const TRANSPARENT_TABLE_HIGHLIGHT = syntaxHighlighting(HighlightStyle.define([
    { tag: tags.string, color: "var(--colors-blue-2)" },
    { tag: tags.propertyName, color: "var(--colors-orange-2)" },
    { tag: tags.brace, color: "var(--colors-grey-2)", },
    { tag: tags.bool, color: "var(--colors-yellow-2)" },
]));

// Extensions/setup must be referentially stable — @uiw/react-codemirror reconfigures the editor
// whenever these props change identity...
const EDITOR_EXTENSIONS = [json(), eagerParsing, measureOnScroll, TRANSPARENT_TABLE_THEME, TRANSPARENT_TABLE_HIGHLIGHT];

const EDITOR_SETUP: BasicSetupOptions = { lineNumbers: false, foldGutter: true };

type EditableValueFieldProps = {
    value: unknown;
    onChange?: (value: string) => void;
    readOnly?: boolean;
};

export const EditableValueField: React.FC<EditableValueFieldProps> = React.memo(function EditabledValueField({ value, onChange = null, readOnly = false }) {
    const handleOnChange = React.useCallback((value: string) => onChange && onChange(value), [onChange]);

    switch (typeof value) {
        default:
            return (
                <div className="code-container">
                    <CodeMirror
                        value={JSON.stringify(value, null, 2)}
                        extensions={EDITOR_EXTENSIONS}
                        basicSetup={EDITOR_SETUP}
                        readOnly={readOnly}
                        onChange={handleOnChange}
                    />
                </div>
            );
    }
});
